/** @format */

import { useEffect, useState, type CSSProperties } from "react";
import "./ProxiedImage.css";

interface ProxiedImageProps {
	src: string;
	alt: string;
	className?: string;
	/** Inline styles applied to the rendered image (and its loading/error stand-ins). */
	style?: CSSProperties;
	loading?: "lazy" | "eager";
}

/**
 * Fetches an image via JS (with Accept: *\/*) so that the old API Gateway
 * returns raw binary instead of base64-encoded text, then renders it in
 * a regular <img> tag with a blob URL.
 */
export const ProxiedImage = ({ src, alt, className, style, loading = "lazy" }: ProxiedImageProps) => {
	const [blobUrl, setBlobUrl] = useState<string | null>(null);
	const [hasError, setHasError] = useState(false);
	const [isLoading, setIsLoading] = useState(true);

	useEffect(() => {
		let isCancelled = false;
		let objectUrl: string | null = null;

		const load = async () => {
			try {
				const res = await fetch(src, {
					headers: { Accept: "*/*" },
				});
				if (!res.ok) {
					throw new Error(`HTTP ${res.status}`);
				}
				const blob = await res.blob();
				if (isCancelled) {
					return;
				}
				objectUrl = URL.createObjectURL(blob);
				setBlobUrl(objectUrl);
			} catch {
				if (!isCancelled) {
					setHasError(true);
				}
			} finally {
				if (!isCancelled) {
					setIsLoading(false);
				}
			}
		};

		setIsLoading(true);
		setHasError(false);
		setBlobUrl(null);
		load();

		return () => {
			isCancelled = true;
			if (objectUrl) {
				URL.revokeObjectURL(objectUrl);
			}
		};
	}, [src]);

	if (hasError) {
		return (
			<div className={`proxied-image-error ${className ?? ""}`} style={style}>
				<span className="proxied-image-error-icon">⚠️</span>
				<span className="proxied-image-error-text">Image unavailable</span>
			</div>
		);
	}

	if (isLoading || !blobUrl) {
		return <div className={`proxied-image-placeholder ${className ?? ""}`} style={style} />;
	}

	return <img src={blobUrl} alt={alt} className={className} style={style} loading={loading} />;
};
